/*
  ns-scroll-restore.js
  Northsquad — robust scroll restoration for collection/search pages.

  Bug fixed: iOS Safari swipe-back-from-PDP often lands at top of the
  collection grid instead of the previous scroll position. Cause: bfcache
  eligibility varies (third-party pixels, no-store headers etc.). When
  bfcache misses, default scroll restoration is unreliable.

  Strategy:
    1. history.scrollRestoration = 'manual' — we own restoration.
    2. Save state on:
       - click on a product card link (capture phase, current page)
       - pagehide (bfcache-safe replacement for 'unload')
    3. Restore on:
       - pageshow with event.persisted === true (bfcache hit)
       - navigation entry type === 'back_forward' (bfcache miss, fresh load)
    4. Multi-frame retry: scrollTo, then re-check up to ~1500ms while
       lazy-loaded grid items resolve and the document grows.
    5. Stale-entry pruning: drop entries older than 1 hour.
    6. Scope: restore only on /collections/* or /search. Save logic is
       global (collection -> PDP -> back) so PDPs also need to listen.
    7. sessionStorage keys prefixed with `ns:scroll:` to avoid collisions.

  No dependencies. Safe to load deferred. Idempotent (guarded by
  window.__nsScrollRestoreBound).
*/
(function () {
  if (window.__nsScrollRestoreBound) return;
  window.__nsScrollRestoreBound = true;

  var PREFIX = 'ns:scroll:';
  var TTL_MS = 60 * 60 * 1000; // 1 hour
  var MAX_RETRY_MS = 1500;
  var FRAME_INTERVAL_MS = 60;

  // Take ownership of scroll restoration as early as the script runs.
  if ('scrollRestoration' in history) {
    try { history.scrollRestoration = 'manual'; } catch (e) {}
  }

  function isCollectionOrSearch() {
    var path = location.pathname || '';
    if (path.indexOf('/collections/') === 0) return true;
    if (path === '/search' || path.indexOf('/search') === 0) return true;
    var body = document.body;
    if (body) {
      if (body.classList && (
        body.classList.contains('template-collection') ||
        body.classList.contains('template-search')
      )) return true;
      var dt = body.getAttribute && body.getAttribute('data-template');
      if (dt === 'collection' || dt === 'search') return true;
    }
    return false;
  }

  function keyFor(pathAndSearch) {
    return PREFIX + pathAndSearch;
  }

  function currentKey() {
    return keyFor(location.pathname + location.search);
  }

  function save(y) {
    try {
      sessionStorage.setItem(currentKey(), JSON.stringify({
        y: y,
        t: Date.now()
      }));
    } catch (e) { /* private mode / quota — ignore */ }
  }

  function read(pathAndSearch) {
    try {
      var raw = sessionStorage.getItem(keyFor(pathAndSearch));
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || typeof obj.y !== 'number') return null;
      if (Date.now() - (obj.t || 0) > TTL_MS) {
        sessionStorage.removeItem(keyFor(pathAndSearch));
        return null;
      }
      return obj;
    } catch (e) { return null; }
  }

  function prune() {
    try {
      var now = Date.now();
      var toRemove = [];
      for (var i = 0; i < sessionStorage.length; i++) {
        var k = sessionStorage.key(i);
        if (!k || k.indexOf(PREFIX) !== 0) continue;
        try {
          var v = JSON.parse(sessionStorage.getItem(k));
          if (!v || now - (v.t || 0) > TTL_MS) toRemove.push(k);
        } catch (e) { toRemove.push(k); }
      }
      for (var j = 0; j < toRemove.length; j++) {
        sessionStorage.removeItem(toRemove[j]);
      }
    } catch (e) {}
  }

  function restoreTo(targetY) {
    var start = (typeof performance !== 'undefined' && performance.now)
      ? performance.now()
      : Date.now();

    function now() {
      return (typeof performance !== 'undefined' && performance.now)
        ? performance.now()
        : Date.now();
    }

    function jumpTo(y) {
      try {
        window.scrollTo({ top: y, left: 0, behavior: 'instant' });
      } catch (e) {
        window.scrollTo(0, y);
      }
    }

    function attempt() {
      jumpTo(targetY);
      var current = window.scrollY || window.pageYOffset || 0;
      if (Math.abs(current - targetY) < 2) return;

      var maxScrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScrollable >= targetY) {
        // Doc is tall enough but we missed somehow — one final hard set, then stop.
        jumpTo(targetY);
        return;
      }
      if (now() - start > MAX_RETRY_MS) return;
      setTimeout(function () { requestAnimationFrame(attempt); }, FRAME_INTERVAL_MS);
    }

    requestAnimationFrame(attempt);
  }

  function tryRestore() {
    if (!isCollectionOrSearch()) return;
    var entry = read(location.pathname + location.search);
    if (!entry || entry.y <= 0) return;
    restoreTo(entry.y);
  }

  function saveCurrent() {
    save(window.scrollY || window.pageYOffset || 0);
  }

  // Capture-phase click on product-card anchors → snapshot current scroll.
  document.addEventListener('click', function (e) {
    if (!isCollectionOrSearch()) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;
    if (a.target && a.target !== '' && a.target !== '_self') return;
    var href = a.getAttribute('href') || '';
    if (href.indexOf('/products/') === -1) return;
    saveCurrent();
  }, true);

  // pagehide is bfcache-friendly (unlike 'unload'). Saves on every nav.
  window.addEventListener('pagehide', saveCurrent);

  // pageshow fires after bfcache restore; persisted=true means we came back via bfcache.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) tryRestore();
  });

  function onReady() {
    prune();

    var navType = null;
    try {
      var entries = performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (entries && entries[0]) navType = entries[0].type;
    } catch (e) {}

    // Legacy fallback for older WebKit where the Navigation Timing Level 2 entry may be missing.
    var legacyBackForward = false;
    try {
      if (performance && performance.navigation && performance.navigation.type === 2) {
        legacyBackForward = true;
      }
    } catch (e) {}

    if (navType === 'back_forward' || legacyBackForward) {
      tryRestore();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
